import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { Input as AntInput, Modal, Checkbox } from "antd";
import { ICity, LocationFormValues } from "../../../types/api/location";
import { useCityCreate, useCityUpdate, useCountryList, useStateList } from "../../../hooks/api/location/hooks";
import ApiSelect from "../../components/ApiSelect";
import { useMessageApi } from "../../../provider/AntdNotification";

interface CityModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItem?: ICity | null;
  qData?: any;
}

const CityModal: React.FC<CityModalProps> = ({
  isOpen,
  onClose,
  selectedItem,
  qData,
}) => {
  const messageApi = useMessageApi();
  const { control, handleSubmit, reset, setValue, watch } = useForm<LocationFormValues>({
    defaultValues: { name: "", slug: "", countryId: undefined, stateId: undefined, status: true },
  });

  const watchCountryId = watch("countryId");

  const createMutation = useCityCreate({
    qData,
    onSuccess: () => {
      messageApi.success("City created successfully");
      onClose();
    },
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to create city"),
  });
  const updateMutation = useCityUpdate({
    qData,
    onSuccess: () => {
      messageApi.success("City updated successfully");
      onClose();
    },
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to update city"),
  });

  const { data: countriesData, refetchWithQuery: refetchCountry } = useCountryList(
    { limit: 100, page: 1 } as any,
    { enabled: isOpen }
  );

  const { data: statesData, refetchWithQuery: refetchState } = useStateList(
    { limit: 100, page: 1, countryId: watchCountryId } as any,
    { enabled: isOpen && !!watchCountryId }
  );

  useEffect(() => {
    if (isOpen) {
      if (selectedItem) {
        reset({
          name: selectedItem.name ?? "",
          slug: selectedItem.slug ?? "",
          countryId: typeof selectedItem.countryId === 'string' ? selectedItem.countryId : (selectedItem.countryId?._id ?? ""),
          stateId: typeof selectedItem.stateId === 'string' ? selectedItem.stateId : (selectedItem.stateId?._id ?? ""),
          status: selectedItem.status ?? true,
        });
      } else {
        reset({ name: "", slug: "", countryId: undefined, stateId: undefined, status: true });
      }
    }
  }, [isOpen, selectedItem, reset]);

  const onSubmit = (data: LocationFormValues) => {
    if (selectedItem) {
      updateMutation.mutate({ id: selectedItem._id, payload: data });
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      title={selectedItem ? "Edit City" : "Add City"}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
        <Controller
          name="countryId"
          control={control}
          rules={{ required: "Country is required" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">
                Country <span className="text-red-500">*</span>
              </label>
              <ApiSelect
                {...field}
                className="w-full"
                placeholder="Select Country"
                status={fieldState.error ? "error" : ""}
                options={countriesData?.data?.map((item) => ({
                  label: item.name,
                  value: item._id,
                }))}
                onChange={(val) => {
                  field.onChange(val);
                  setValue("stateId", undefined);
                }}
                refetchWithQuery={refetchCountry}
                baseQuery={{ limit: 100, page: 1 }}
              />
              {fieldState.error && (
                <p className="text-red-500 text-xs mt-1">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="stateId"
          control={control}
          rules={{ required: "State is required" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">
                State <span className="text-red-500">*</span>
              </label>
              <ApiSelect
                {...field}
                className="w-full"
                placeholder="Select State"
                status={fieldState.error ? "error" : ""}
                options={statesData?.data?.map((item) => ({
                  label: item.name,
                  value: item._id,
                }))}
                disabled={!watchCountryId}
                refetchWithQuery={refetchState}
                baseQuery={{ limit: 100, page: 1, countryId: watchCountryId }}
              />
              {fieldState.error && (
                <p className="text-red-500 text-xs mt-1">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="name"
          control={control}
          rules={{ required: "Name is required" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <AntInput
                {...field}
                placeholder="Enter city name"
                status={fieldState.error ? "error" : ""}
                onChange={(e) => {
                  field.onChange(e);
                  if (!selectedItem) {
                    setValue(
                      "slug",
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/(^-|-$)+/g, "")
                    );
                  }
                }}
              />
              {fieldState.error && (
                <p className="text-red-500 text-xs mt-1">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="slug"
          control={control}
          rules={{ required: "Slug is required" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">
                Slug <span className="text-red-500">*</span>
              </label>
              <AntInput {...field} placeholder="city-slug" status={fieldState.error ? "error" : ""} />
              {fieldState.error && (
                <p className="text-red-500 text-xs mt-1">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="status"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)}>
              Active Status
            </Checkbox>
          )}
        />

        <div className="flex justify-end gap-2 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            disabled={isPending}
          >
            {isPending && (
              <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            )}
            {selectedItem ? "Update" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CityModal;
